import React from 'react';
import { Box, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { HouseholdModel } from '../../services/household-retirement-service';
import { NumField, produce } from './fields';
import { CLASS_LABELS } from './format';

const ORDER = ['equity', 'fixed_income', 'cash', 'crypto', 'commodity', 'private', 'other'];

/** Economic assumptions: returns and volatility by asset class, inflation,
 * salary and home growth, the withdrawal strategy, and the surplus sweep. */
export const AssumptionsEditor: React.FC<{ model: HouseholdModel; onChange: (m: HouseholdModel) => void }> = ({ model, onChange }) => {
  const set = (fn: (d: HouseholdModel) => void) => onChange(produce(model, fn));
  const a = model.assumptions;
  return (
    <Stack spacing={2} data-testid="assumptions-editor">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
        <NumField label="Inflation %" value={a.inflation_pct} sx={{ width: 130 }} onChange={(v) => set((d) => { d.assumptions.inflation_pct = v ?? 2.5; })} />
        <NumField label="Salary growth %" value={a.salary_growth_pct} sx={{ width: 160 }} onChange={(v) => set((d) => { d.assumptions.salary_growth_pct = v ?? 3; })} />
        <NumField label="Home growth %" value={model.assets.home_growth_pct ?? 3} sx={{ width: 160 }} onChange={(v) => set((d) => { d.assets.home_growth_pct = v; })} />
        <NumField label="Vehicle depreciation %" value={model.assets.vehicle_depreciation_pct} sx={{ width: 200 }}
          onChange={(v) => set((d) => { d.assets.vehicle_depreciation_pct = v ?? 15; })} />
        <NumField label="Withdrawal rate %" value={a.withdrawal_rate_pct} sx={{ width: 170 }} onChange={(v) => set((d) => { d.assumptions.withdrawal_rate_pct = v ?? 4; })} />
        <TextField select size="small" label="Withdrawal strategy" value={a.withdrawal_strategy} sx={{ minWidth: 300 }}
          onChange={(e) => set((d) => { d.assumptions.withdrawal_strategy = e.target.value as 'fixed' | 'guardrails'; })}>
          <MenuItem value="fixed">Fixed real spending</MenuItem>
          <MenuItem value="guardrails">Guardrails (±10% when off by 20%)</MenuItem>
        </TextField>
        <NumField label="Plan to age" value={a.end_age} sx={{ width: 130 }} onChange={(v) => set((d) => { d.assumptions.end_age = v ?? 95; })} />
        <NumField label="Success target %" value={a.success_target_pct} sx={{ width: 160 }} onChange={(v) => set((d) => { d.assumptions.success_target_pct = v ?? 90; })} />
        <NumField label="RESP return %" value={a.resp_return_pct} sx={{ width: 150 }} onChange={(v) => set((d) => { d.assumptions.resp_return_pct = v ?? 5; })} />
        <NumField label="Prime %" value={a.prime_pct} sx={{ width: 130 }} helperText="flat path"
          onChange={(v) => set((d) => { d.assumptions.prime_pct = v ?? 4.45; })} />
        <ToggleButtonGroup size="small" exclusive value={a.prime_shift_pct ?? 0} aria-label="Prime scenario"
          onChange={(_, v) => v !== null && set((d) => { d.assumptions.prime_shift_pct = v; })}>
          <ToggleButton value={-1}>Prime −1%</ToggleButton>
          <ToggleButton value={0}>Base</ToggleButton>
          <ToggleButton value={1}>Prime +1%</ToggleButton>
        </ToggleButtonGroup>
        <NumField label="Default loan rate %" value={a.default_loan_rate_pct} sx={{ width: 180 }} onChange={(v) => set((d) => { d.assumptions.default_loan_rate_pct = v ?? 4.95; })} />
        <NumField label="Surplus invested %" value={model.contributions.surplus_sweep_pct ?? 100} sx={{ width: 180 }}
          helperText="the rest is treated as spent"
          onChange={(v) => set((d) => { d.contributions.surplus_sweep_pct = v ?? 0; })} />
        <NumField label="Extra invested $/mo" value={model.contributions.extra_monthly ?? 0} sx={{ width: 180 }}
          onChange={(v) => set((d) => { d.contributions.extra_monthly = v ?? 0; })} />
      </Stack>
      <Box>
        <Typography variant="subtitle2" gutterBottom>Expected return and volatility by asset class (annual %)</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {ORDER.filter((k) => a.returns[k]).map((k) => (
            <Stack key={k} direction="row" spacing={0.5} alignItems="center" sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 96 }}>{CLASS_LABELS[k] ?? k}</Typography>
              <NumField label="Return" value={a.returns[k].mean_pct} sx={{ width: 90 }}
                inputProps={{ 'aria-label': `${k} return` }}
                onChange={(v) => set((d) => { d.assumptions.returns[k].mean_pct = v ?? 0; })} />
              <NumField label="Stdev" value={a.returns[k].stdev_pct} sx={{ width: 90 }}
                inputProps={{ 'aria-label': `${k} stdev` }}
                onChange={(v) => set((d) => { d.assumptions.returns[k].stdev_pct = v ?? 0; })} />
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};
